// Root of the v2 UI: auth guard → data provider → app shell → routed pages.
//
// Everything is a real route. Drill-ins are pages with breadcrumbs — there is
// no modal stack and no "historical takeover" mode.

import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Alert, Box } from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import AppShell from './AppShell';
import { duration, easing, fadeSlideUp, reduceMotionSx } from './theme/motion';
import GlobalSearch from './components/GlobalSearch';
import { DeploymentDataProvider, useDeploymentData } from './hooks/useDeploymentData';
import OverviewPage from './views/OverviewPage';
import ServicesDirectory from './views/ServicesDirectory';
import AppPage from './views/AppPage';
import ServicePage from './views/ServicePage';
import ActivityPage from './views/ActivityPage';
import ReleasesPage from './views/ReleasesPage';
import EnvironmentsPage from './views/EnvironmentsPage';
import EnvironmentPage from './views/EnvironmentPage';
import JobsPage from './views/JobsPage';
import JobPage from './views/JobPage';
import InfraOverviewPage from './views/infra/InfraOverviewPage';
import ModulesPage from './views/infra/ModulesPage';
import ModulePage from './views/infra/ModulePage';
import AdoptionPage from './views/infra/AdoptionPage';

interface DashboardV2Props {
  mode: 'light' | 'dark';
  toggleColorMode: () => void;
}

/**
 * Translate legacy (classic UI) query-param URLs into v2 routes so old shared
 * links keep working when the flag is on. Returns null when nothing applies.
 */
export const legacyParamsToV2Path = (search: string): string | null => {
  const params = new URLSearchParams(search);
  const openApp = params.get('open_app');
  const openSvc = params.get('open_svc');
  const job = params.get('job');
  const view = params.get('view');

  if (openApp && openSvc) {
    return `/services/${encodeURIComponent(openApp)}/${encodeURIComponent(openSvc)}`;
  }
  if (openApp) {
    return `/services/${encodeURIComponent(openApp)}`;
  }
  if (job) {
    return `/jobs?job=${encodeURIComponent(job)}`;
  }
  if (view === 'jobs') return '/jobs';
  if (view === 'dynamic') return '/environments';
  if (view === 'timeline') return '/releases';
  return null;
};

/** Redirects legacy query-param deep links once, at the root route. */
const LegacyRedirect: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const location = useLocation();
  const target = legacyParamsToV2Path(location.search);
  if (target) return <Navigate to={target} replace />;
  return children;
};

const ShellWithData: React.FC<{
  mode: 'light' | 'dark';
  toggleColorMode: () => void;
  userName?: string;
}> = ({ mode, toggleColorMode, userName }) => {
  const { wsStatus, reconnect, error, clearError, loading, loadingProgress } = useDeploymentData();
  const location = useLocation();

  return (
    <AppShell
      mode={mode}
      toggleColorMode={toggleColorMode}
      wsStatus={wsStatus}
      onReconnect={reconnect}
      userName={userName}
      loading={loading}
      loadingProgress={loadingProgress}
      topbarCenter={<GlobalSearch />}
    >
      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {/* Re-key on path so each page plays the entrance animation on navigation. */}
      <Box
        key={location.pathname}
        sx={{
          animation: `${fadeSlideUp} ${duration.slow}ms ${easing.emphasized} both`,
          ...reduceMotionSx,
        }}
      >
        <Routes>
          <Route
            path="/"
            element={
              <LegacyRedirect>
                <OverviewPage />
              </LegacyRedirect>
            }
          />
          <Route path="/services" element={<ServicesDirectory />} />
          <Route path="/services/:app" element={<AppPage />} />
          <Route path="/services/:app/:jobId" element={<ServicePage />} />
          <Route path="/activity" element={<ActivityPage />} />
          <Route path="/releases" element={<ReleasesPage />} />
          <Route path="/environments" element={<EnvironmentsPage />} />
          <Route path="/environments/:env" element={<EnvironmentPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/jobs/:app/:jobId" element={<JobPage />} />
          <Route path="/infra" element={<InfraOverviewPage />} />
          <Route path="/infra/modules" element={<ModulesPage />} />
          <Route path="/infra/modules/*" element={<ModulePage />} />
          <Route path="/infra/adoption" element={<AdoptionPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Box>
    </AppShell>
  );
};

const DashboardV2: React.FC<DashboardV2Props> = ({ mode, toggleColorMode }) => {
  const { oktaAuth, authState } = useOktaAuth();
  const [userName, setUserName] = useState<string>('');

  useEffect(() => {
    if (!authState) return;
    if (!authState.isAuthenticated) {
      oktaAuth.signInWithRedirect();
    }
  }, [authState, oktaAuth]);

  useEffect(() => {
    if (authState?.isAuthenticated && authState.idToken?.claims) {
      const claims = authState.idToken.claims;
      setUserName((claims.name || claims.given_name || claims.email || '') as string);
    }
  }, [authState]);

  if (!authState || !authState.isAuthenticated) {
    return <Box />;
  }

  return (
    <DeploymentDataProvider>
      <ShellWithData mode={mode} toggleColorMode={toggleColorMode} userName={userName} />
    </DeploymentDataProvider>
  );
};

export default DashboardV2;
